import { Component } from 'preact';
import { api } from '../../utils/serverClient';
import { isDevBuild } from '../../utils/devMode';
import { getUpdateChannel } from '../../utils/updateChannel';
import { onMaintenanceChange } from '../../utils/maintenanceState';
import {
  TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  INPUT_STYLE, GOLD_BTN, DANGER_BTN,
} from '../../lib/medievalTheme';
import { Section, AdminButton } from '../AdminPanel';

// Mapping from the desktop client's "release channel" (which decides
// which game server it talks to) to the corresponding Railway service
// the deploy should target. If the admin schedules from a client
// connected to beta, the auto-deploy hits the beta Railway service;
// connected to stable, it hits production. The `local` channel has no
// remote target, so auto-deploy is disabled in that mode.
const CHANNEL_TO_RAILWAY_SERVICE = {
  stable: 'valkenhall-server',
  beta: 'valkenhall-server-beta',
  local: null,
};
const CHANNEL_DISPLAY_NAME = {
  stable: 'production',
  beta: 'beta',
  local: 'local',
};

// Dev-only API surface that schedules a `railway up --detach` to fire
// at the same instant the maintenance notice is set for. The endpoint
// is served by the local Electron process (not the game server), so
// these requests must hit the renderer's own origin. Bare paths only.
async function localFetch(path, init) {
  const res = await fetch(path, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}
const devDeployApi = {
  get: () => localFetch('/api/dev/scheduled-deploy'),
  schedule: (payload) => localFetch('/api/dev/scheduled-deploy', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  }),
  cancel: () => localFetch('/api/dev/scheduled-deploy', { method: 'DELETE' }),
};

// Pad an integer with a leading zero for the datetime-local format.
function pad(n) { return String(n).padStart(2, '0'); }

// datetime-local inputs work in the user's local zone but ignore the
// offset. The value we read back is "YYYY-MM-DDTHH:mm" without a Z.
// Convert to an ISO string by treating it as local time, then sending
// the absolute instant the server should fire on.
function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultScheduledInput() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return isoToLocalInput(d.toISOString());
}

function normalizeVersion(raw) {
  return (raw || '').trim().replace(/^v/, '');
}

export default class AdminMaintenanceTab extends Component {
  state = {
    notice: null,
    loading: true,
    scheduledLocal: defaultScheduledInput(),
    message: '',
    saving: false,
    error: null,
    imminentSeconds: 30,
    triggering: false,
    // Dev-only auto-deploy fields. `autoDeploy` is the form toggle;
    // `localDeploy` is the current scheduled job reported by the
    // local Electron server.
    autoDeploy: true,
    localDeploy: null,
    // Version-compat coordination. `releaseClient` cuts a tag in the
    // client repo (and waits for the GitHub release workflow to
    // finish) before running the Railway deploy below. `minClientVersion`
    // is set as a Railway env var on the target service so the new
    // server rejects any client older than this version.
    releaseClient: false,
    clientVersion: '',
    minClientVersion: '',
  };

  componentDidMount() {
    this.refresh();
    if (isDevBuild()) {
      this.refreshLocalDeploy();
      // Poll the local deploy state so the admin can watch the
      // build/exit transitions without having to reopen the panel.
      this._devPoll = setInterval(this.refreshLocalDeploy, 4000);
    }
    // Subscribe to update-phase changes so we can refetch the
    // server's notice the moment the new container is detected. This
    // is the *only* signal that's actually synchronised with the
    // post-restart state. `launched` (railway-up exit) fires too
    // early, the new server isn't serving yet.
    this._maintUnsub = onMaintenanceChange(({ updatePhase }) => {
      if (updatePhase === 'complete' && this._lastSeenPhase !== 'complete') {
        // New server is reachable. Refetch so the "Active notice"
        // card reflects the auto-cleared state.
        this.refresh();
      }
      this._lastSeenPhase = updatePhase;
    });
  }

  componentWillUnmount() {
    if (this._devPoll) clearInterval(this._devPoll);
    if (this._maintUnsub) this._maintUnsub();
  }

  refresh = async () => {
    try {
      const { notice } = await api.get('/admin/maintenance');
      this.setState({ notice: notice || null, loading: false, error: null });
    } catch (err) {
      this.setState({ loading: false, error: err.message });
    }
  };

  refreshLocalDeploy = async () => {
    try {
      const { deploy } = await devDeployApi.get();
      this.setState({ localDeploy: deploy || null });
    } catch (_) {
      // Endpoint may 403 in shipped builds or if the local server
      // hasn't booted yet. Fail quietly.
    }
  };

  handleSchedule = async () => {
    const iso = localInputToIso(this.state.scheduledLocal);
    if (!iso) {
      this.setState({ error: 'Pick a valid date/time' });
      return;
    }
    this.setState({ saving: true, error: null });
    try {
      const { notice } = await api.post('/admin/maintenance', {
        scheduledAt: iso,
        message: this.state.message.trim() || null,
      });
      this.setState({ notice, saving: false });
      this.props.addLog?.(`Maintenance scheduled for ${new Date(iso).toLocaleString()}`);

      if (isDevBuild() && this.state.autoDeploy) {
        await this.scheduleLocalAutoDeploy(iso);
      }
    } catch (err) {
      this.setState({ saving: false, error: err.message });
    }
  };

  // Tells the local Electron server to fire `railway up --detach` at the
  // same moment the notice is set for, so the actual restart lines up
  // with the countdown players see. The Railway service is derived from
  // the desktop client's current release channel: beta client → beta
  // service, stable client → production.
  scheduleLocalAutoDeploy = async (iso) => {
    const channel = getUpdateChannel();
    const service = CHANNEL_TO_RAILWAY_SERVICE[channel];
    if (!service) {
      this.props.addLog?.(`Auto-deploy skipped: no Railway service for channel '${channel}'`);
      return;
    }
    if (channel === 'stable' && !confirm(`Auto-deploy will target PRODUCTION (${service}) at the scheduled time. Continue?`)) {
      this.props.addLog?.('Auto-deploy cancelled (production target rejected)');
      return;
    }

    const payload = { at: iso, service };
    const minVer = normalizeVersion(this.state.minClientVersion);
    if (minVer) payload.minClientVersion = minVer;
    if (this.state.releaseClient) {
      const v = normalizeVersion(this.state.clientVersion);
      if (!v) {
        this.props.addLog?.('Client release skipped: no version entered');
      } else {
        payload.releaseClient = { version: v };
      }
    }

    try {
      const { deploy } = await devDeployApi.schedule(payload);
      this.setState({ localDeploy: deploy || null });
      const extras = [];
      if (payload.releaseClient) extras.push(`release v${payload.releaseClient.version}`);
      if (payload.minClientVersion) extras.push(`min-client=${payload.minClientVersion}`);
      const extraNote = extras.length ? ` (${extras.join(', ')})` : '';
      this.props.addLog?.(`Local auto-deploy scheduled for ${new Date(iso).toLocaleString()} → ${service}${extraNote}`);
    } catch (devErr) {
      this.props.addLog?.(`Auto-deploy schedule failed: ${devErr.message}`);
      this.setState({ error: `Notice saved, but auto-deploy failed: ${devErr.message}` });
    }
  };

  handleClear = async () => {
    this.setState({ saving: true, error: null });
    try {
      await api.delete('/admin/maintenance');
      this.setState({ notice: null, saving: false });
      this.props.addLog?.('Maintenance notice cleared');
      // Cancel any pending local deploy alongside the notice. Leaving
      // a deploy queued after the notice is cleared would be a footgun.
      if (isDevBuild() && this.state.localDeploy) {
        try {
          await devDeployApi.cancel();
          this.setState({ localDeploy: null });
          this.props.addLog?.('Local auto-deploy cancelled');
        } catch (_) {}
      }
    } catch (err) {
      this.setState({ saving: false, error: err.message });
    }
  };

  handleCancelLocalDeploy = async () => {
    try {
      await devDeployApi.cancel();
      this.setState({ localDeploy: null });
      this.props.addLog?.('Local auto-deploy cancelled');
    } catch (err) {
      this.setState({ error: err.message });
    }
  };

  handleTriggerImminent = async () => {
    const seconds = Math.max(5, Math.min(300, parseInt(this.state.imminentSeconds, 10) || 30));
    if (!confirm(`Broadcast "server going down in ${seconds}s" to every connected client?`)) return;
    this.setState({ triggering: true, error: null });
    try {
      await api.post('/admin/maintenance/imminent', {
        seconds,
        message: this.state.notice?.message || this.state.message.trim() || null,
      });
      this.props.addLog?.(`Imminent shutdown broadcast (${seconds}s)`);
    } catch (err) {
      this.setState({ error: err.message });
    } finally {
      this.setState({ triggering: false });
    }
  };

  render() {
    const { notice, loading, scheduledLocal, message, saving, error, imminentSeconds, triggering, autoDeploy, localDeploy, releaseClient, clientVersion, minClientVersion } = this.state;
    const dev = isDevBuild();
    const channel = dev ? getUpdateChannel() : null;
    const targetService = dev ? CHANNEL_TO_RAILWAY_SERVICE[channel] : null;
    const channelLabel = dev ? CHANNEL_DISPLAY_NAME[channel] || channel : null;
    const isProdTarget = channel === 'stable';

    return (
      <>
        <Section title="Scheduled Maintenance">
          {loading ? (
            <p className="text-xs" style={{ color: TEXT_MUTED }}>Loading…</p>
          ) : notice ? (
            <div className="mb-3 px-3 py-2" style={{ border: `1px solid ${ACCENT_GOLD}55`, borderRadius: 'var(--radius-md)' }}>
              <div className="text-xs" style={{ color: ACCENT_GOLD }}>
                Active notice
              </div>
              <div className="text-sm mt-1" style={{ color: TEXT_BODY }}>
                Restart at <strong>{new Date(notice.scheduledAt).toLocaleString()}</strong>
              </div>
              {notice.message ? (
                <div className="text-xs mt-1" style={{ color: TEXT_BODY }}>
                  Reason: {notice.message}
                </div>
              ) : null}
              <div className="mt-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all"
                  style={DANGER_BTN}
                  onClick={this.handleClear}
                  disabled={saving}
                >
                  {saving ? 'Clearing…' : 'Cancel notice'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs mb-3" style={{ color: TEXT_MUTED }}>
              No maintenance currently scheduled. Set a time below to warn every connected player.
            </p>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-xs" style={{ color: TEXT_MUTED }}>Restart at (your local time)</label>
            <input
              type="datetime-local"
              value={scheduledLocal}
              onInput={(e) => this.setState({ scheduledLocal: e.target.value })}
              className="px-3 py-1.5 text-xs"
              style={INPUT_STYLE}
            />
            <label className="text-xs mt-1" style={{ color: TEXT_MUTED }}>Reason (optional, shown to players)</label>
            <input
              type="text"
              placeholder="e.g. New card set + balance changes"
              value={message}
              onInput={(e) => this.setState({ message: e.target.value })}
              className="px-3 py-1.5 text-xs"
              style={INPUT_STYLE}
            />
            {dev ? (
              <label className="flex items-start gap-2 mt-1 cursor-pointer" title="Dev-only: requires Railway CLI logged in on this machine and the valkenhall-server repo as a sibling checkout">
                <input
                  type="checkbox"
                  checked={autoDeploy}
                  onChange={(e) => this.setState({ autoDeploy: !!e.target.checked })}
                  className="mt-0.5"
                  disabled={!targetService}
                />
                <span className="text-xs" style={{ color: TEXT_BODY, lineHeight: 1.4 }}>
                  Also run <code style={{ color: ACCENT_GOLD }}>railway up --detach</code> at this time so the actual restart lines up with the notice.
                  {targetService ? (
                    <>
                      {' '}Target: <strong style={{ color: isProdTarget ? '#fca5a5' : ACCENT_GOLD }}>{targetService}</strong>
                      <span style={{ color: TEXT_MUTED }}> (current channel: {channelLabel})</span>
                      {isProdTarget ? (
                        <div className="text-xs mt-0.5" style={{ color: '#ef4444' }}>
                          ⚠ This will deploy to PRODUCTION. You'll be asked to confirm.
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span style={{ color: TEXT_MUTED }}> (no Railway target for the <strong>{channelLabel}</strong> channel. Auto-deploy disabled)</span>
                  )}
                </span>
              </label>
            ) : null}
            {dev && autoDeploy && targetService ? (
              <div
                className="mt-1 px-3 py-2 flex flex-col gap-2"
                style={{ border: `1px solid ${ACCENT_GOLD}33`, borderRadius: 'var(--radius-md)' }}
              >
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={releaseClient}
                    onChange={(e) => this.setState({ releaseClient: !!e.target.checked })}
                    className="mt-0.5"
                  />
                  <span className="text-xs" style={{ color: TEXT_BODY, lineHeight: 1.4 }}>
                    Cut a client release first. Tags <code style={{ color: ACCENT_GOLD }}>vX.Y.Z</code> in the client repo, waits for the GitHub release workflow to finish, then deploys the server. Use this when a server change makes older clients incompatible.
                  </span>
                </label>
                {releaseClient ? (
                  <div className="flex flex-col gap-1 pl-6">
                    <label className="text-xs" style={{ color: TEXT_MUTED }}>Client version to tag (without "v")</label>
                    <input
                      type="text"
                      placeholder="e.g. 0.9.5"
                      value={clientVersion}
                      onInput={(e) => this.setState({ clientVersion: e.target.value })}
                      className="px-3 py-1.5 text-xs"
                      style={INPUT_STYLE}
                    />
                  </div>
                ) : null}
                <div className="flex flex-col gap-1 pl-6">
                  <label className="text-xs" style={{ color: TEXT_MUTED }}>
                    Min client version after deploy (optional. Sets <code style={{ color: ACCENT_GOLD }}>MIN_CLIENT_VERSION</code> on Railway)
                  </label>
                  <input
                    type="text"
                    placeholder={releaseClient && clientVersion ? `e.g. ${clientVersion.trim().replace(/^v/, '')}` : 'e.g. 0.9.5'}
                    value={minClientVersion}
                    onInput={(e) => this.setState({ minClientVersion: e.target.value })}
                    className="px-3 py-1.5 text-xs"
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
            ) : null}
            <div>
              <button
                type="button"
                className="px-4 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.02]"
                style={GOLD_BTN}
                onClick={this.handleSchedule}
                disabled={saving}
              >
                {saving ? 'Saving…' : notice ? 'Replace notice' : 'Schedule maintenance'}
              </button>
            </div>
          </div>
          {error ? (
            <div className="mt-2 text-xs" style={{ color: '#ef4444' }}>{error}</div>
          ) : null}

          {dev && localDeploy ? (
            <div className="mt-3 px-3 py-2" style={{ border: `1px solid ${ACCENT_GOLD}55`, borderRadius: 'var(--radius-md)' }}>
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <div className="text-xs" style={{ color: ACCENT_GOLD }}>
                    Local auto-deploy ({localDeploy.state})
                  </div>
                  <div className="text-xs mt-1" style={{ color: TEXT_BODY }}>
                    Fires <strong>{new Date(localDeploy.at).toLocaleString()}</strong>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                    Repo: <code>{localDeploy.repoPath}</code>
                  </div>
                  {localDeploy.service ? (
                    <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                      Service: <code style={{ color: localDeploy.service === 'valkenhall-server' ? '#fca5a5' : ACCENT_GOLD }}>{localDeploy.service}</code>
                    </div>
                  ) : null}
                  {localDeploy.releaseClient?.version ? (
                    <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                      Client release: <code style={{ color: ACCENT_GOLD }}>v{localDeploy.releaseClient.version}</code>
                    </div>
                  ) : null}
                  {localDeploy.minClientVersion ? (
                    <div className="text-xs mt-0.5" style={{ color: TEXT_MUTED }}>
                      Min client: <code style={{ color: ACCENT_GOLD }}>{localDeploy.minClientVersion}</code>
                    </div>
                  ) : null}
                </div>
                {localDeploy.state === 'waiting' ? (
                  <button
                    type="button"
                    className="px-2.5 py-1 text-[0.6875cqw] font-semibold"
                    style={DANGER_BTN}
                    onClick={this.handleCancelLocalDeploy}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
              {localDeploy.log?.length ? (
                <pre
                  className="mt-2 text-[0.6cqw] leading-tight overflow-y-auto"
                  style={{ maxHeight: '8cqw', color: TEXT_MUTED, fontFamily: 'ui-monospace, SFMono-Regular, monospace', whiteSpace: 'pre-wrap' }}
                >
                  {localDeploy.log.map((entry) => entry.line).slice(-30).join('\n')}
                </pre>
              ) : null}
            </div>
          ) : null}
        </Section>

        <Section title="Trigger Imminent Shutdown">
          <p className="text-xs mb-3" style={{ color: TEXT_MUTED, lineHeight: 1.5 }}>
            Push the strong full-screen overlay to every connected client right now. Use this immediately before pushing a deploy. The SIGTERM handler also fires this automatically with a 15-second window when Railway tears down the container.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="5"
              max="300"
              value={imminentSeconds}
              onInput={(e) => this.setState({ imminentSeconds: e.target.value })}
              className="w-20 px-2 py-1 text-xs"
              style={INPUT_STYLE}
            />
            <span className="text-xs" style={{ color: TEXT_MUTED }}>seconds</span>
            <AdminButton onClick={this.handleTriggerImminent} disabled={triggering}>
              {triggering ? 'Broadcasting…' : 'Broadcast imminent'}
            </AdminButton>
          </div>
        </Section>
      </>
    );
  }
}
